/**
 * VaultSaveConsentSheet — host-rendered confirmation for a plugin saving into
 * the user's OWN Fias files (the `userVisible` half of `vault_documents_upload`).
 *
 * The counterpart to `VaultAccessGrantSheet`: that one governs a plugin READING
 * documents the user already has, this one governs a plugin WRITING a document
 * the user will keep. Both are host chrome the plugin can neither see nor
 * script — sanctioned transport exceptions, see docs/capability-placement.md
 * § "Transport exceptions".
 *
 * Deliberately ONE step, not two. The read sheet needs a consent step because a
 * grant is standing and revocable; a save is a single act with a single
 * outcome, and stacking a second screen on it would train people to click
 * through the one that matters. What this screen owes the user is accuracy
 * about the destination, which is why it names the folder.
 *
 * Scope note: confirming here authorizes exactly this file. There is no
 * "always allow" — the plugin gets no standing write capability, which is what
 * lets `vault:user-documents:write` be a narrower grant than its read sibling.
 */
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FileDown, FolderOpen } from 'lucide-react';

/** What the plugin asked to save. Metadata only — the bytes never reach here. */
export interface VaultSaveRequest {
  /** File name the plugin proposes. UNTRUSTED — see `sanitizeForDisplay`. */
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  /** True when this replaces an existing document rather than creating one. */
  isReplace: boolean;
}

export type VaultSaveResolution = { confirmed: true } | { canceled: true };

interface VaultSaveConsentSheetProps {
  open: boolean;
  /** Display name of the arche asking. Falls back to generic copy when null. */
  archeName: string | null;
  /**
   * Folder the file will land in (`/My-Fias/<label>/`). Null when the host
   * could not resolve a label — the sheet then omits the destination line
   * rather than guessing, and the server refuses the save anyway.
   */
  folderLabel: string | null;
  request: VaultSaveRequest;
  onResolve: (result: VaultSaveResolution) => void;
}

/**
 * Everything on this screen is plugin-supplied, and this is the one screen whose
 * job is to be accurate about what is being saved and where — so treat every
 * field as hostile. React escapes markup but not presentation: a bidi override
 * reverses an extension (`\u202E` renders "gnp.exe" as "exe.png"), control
 * characters hide text, and an unbounded string pushes the destination line out
 * of view.
 *
 * DO NOT assume this matches what gets STORED. The server's `sanitizeName`
 * strips a narrower set (C0/DEL only) and maps slashes to `_`, so the two
 * deliberately diverge — see `previewStoredName` for the name the user is
 * actually approving.
 */
function scrubForDisplay(value: string, maxLength: number): string {
  return (
    value
      // C0/C1 controls, plus the bidi override/embedding/isolate family.
      // eslint-disable-next-line no-control-regex
      .replace(/[\u0000-\u001f\u007f-\u009f\u200e\u200f\u202a-\u202e\u2066-\u2069]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, maxLength)
  );
}

/**
 * The name as the SERVER will store it, then scrubbed for safe rendering.
 *
 * Showing the raw plugin string would let the user approve `cat\u202Egnp.exe`
 * while the row stores a name that renders as `catexe.png` everywhere else in
 * Fias Files — approval of one thing, delivery of another. So mirror the
 * server's transform first (`sanitizeName`: strip C0/DEL, `/` and `\` → `_`,
 * trim, cap at 255), and only then scrub for display. What the user reads is
 * then the name they will actually see in their files.
 *
 * The mirror is intentional duplication across a process boundary; the
 * alternative is a sheet that lies. Keep it in step with `sanitizeName` in
 * `services/api-service/src/routes/plugins/vault-documents-bridge-handlers.ts`.
 */
function previewStoredName(rawName: string): string {
  const asStored = rawName
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, '')
    .replace(/[/\\]/g, '_')
    .trim()
    .slice(0, 255);
  const display = scrubForDisplay(asStored, 120);
  return display || 'Untitled file';
}

/** Human file size. Deliberately coarse — this is a reassurance, not a spec. */
function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function VaultSaveConsentSheet({
  open,
  archeName,
  folderLabel,
  request,
  onResolve,
}: VaultSaveConsentSheetProps) {
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setBusy(false);
  }, [open]);

  const appLabel = archeName ?? 'This app';
  const displayName = previewStoredName(request.fileName);
  // `mimeType` is plugin-controlled too, and sits directly above the
  // destination line — an unbounded or bidi-laden value there can push the
  // folder out of view or forge a second one. Same treatment, tighter bound.
  const displayMime = scrubForDisplay(request.mimeType, 60);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Dismissing (Esc, overlay click, the X) is a decline, not a no-op —
        // the plugin's promise is still pending and must always settle.
        if (!next && !busy) onResolve({ canceled: true });
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileDown className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
            {request.isReplace ? 'Save over your file?' : 'Save to your Fias files?'}
          </DialogTitle>
          <DialogDescription>
            {request.isReplace
              ? `${appLabel} wants to replace one of your files with a new version.`
              : `${appLabel} wants to save a file you'll be able to find and reuse.`}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border bg-muted/40 p-3 text-sm">
          <div className="truncate font-medium" title={displayName}>
            {displayName}
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {formatSize(request.sizeBytes)}
            {displayMime ? ` · ${displayMime}` : ''}
          </div>
          {folderLabel && (
            <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
              <FolderOpen className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span className="truncate">Saves to My Fias / {folderLabel}</span>
            </div>
          )}
        </div>

        <p className="text-xs text-muted-foreground">
          {request.isReplace
            ? 'The previous version is kept in the file’s history.'
            : 'It goes in your own files, so other apps can use it and it stays after you close this one.'}
        </p>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onResolve({ canceled: true })}>
            Not now
          </Button>
          <Button onClick={() => onResolve({ confirmed: true })}>
            {request.isReplace ? 'Replace' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
